// src/components/Inbox.tsx
import { buildSubscriber } from "@novu/js/internal";
import React, { useMemo } from "react";
import { useNotificationOutlets } from "../hooks/internal/useNotificationOutlets.js";
import { useOutletRenderer } from "../hooks/internal/useOutletRenderer.js";
import { InternalNovuProvider, useNovu, useUnsafeNovu } from "../hooks/NovuProvider.js";
import { Mounter } from "./Mounter.js";
import { NovuUI } from "./NovuUI.js";
import { jsx } from "react/jsx-runtime";
var DefaultInbox = (props) => {
  const {
    open,
    renderBell,
    onNotificationClick,
    onPrimaryActionClick,
    onSecondaryActionClick,
    placement,
    placementOffset
  } = props;
  const outlets = useNotificationOutlets(props);
  const bellOutlet = useOutletRenderer(renderBell);
  const mountProps = useMemo(
    () => ({
      ...outlets,
      open,
      renderBell: bellOutlet,
      onNotificationClick,
      onPrimaryActionClick,
      onSecondaryActionClick,
      placementOffset,
      placement
    }),
    [
      outlets,
      open,
      bellOutlet,
      onNotificationClick,
      onPrimaryActionClick,
      onSecondaryActionClick,
      placementOffset,
      placement
    ]
  );
  return /* @__PURE__ */ jsx(Mounter, { name: "Inbox", props: mountProps });
};
var Inbox = React.memo((props) => {
  const { subscriberId, ...propsWithoutSubscriberId } = props;
  const subscriber = useMemo(
    () => buildSubscriber({ subscriberId: props.subscriberId, subscriber: props.subscriber }),
    [props.subscriberId, props.subscriber]
  );
  const applicationIdentifier = props.applicationIdentifier ? props.applicationIdentifier : "";
  const novu = useUnsafeNovu();
  if (novu) {
    return /* @__PURE__ */ jsx(InboxChild, { ...propsWithoutSubscriberId, applicationIdentifier, subscriber });
  }
  const providerProps = {
    applicationIdentifier,
    subscriberHash: props.subscriberHash,
    contextHash: props.contextHash,
    backendUrl: props.backendUrl,
    socketUrl: props.socketUrl,
    socketOptions: props.socketOptions,
    subscriber,
    defaultSchedule: props.defaultSchedule,
    context: props.context
  };
  return /* @__PURE__ */ jsx(InternalNovuProvider, { ...providerProps, children: /* @__PURE__ */ jsx(InboxChild, { ...propsWithoutSubscriberId, applicationIdentifier, subscriber }) });
});
var InboxChild = React.memo((props) => {
  const {
    localization,
    appearance,
    tabs,
    preferencesFilter,
    preferenceGroups,
    preferencesSort,
    routerPush,
    applicationIdentifier = "",
    // for keyless we provide an empty string, the api will generate a identifier
    subscriberId,
    subscriberHash,
    contextHash,
    backendUrl,
    socketUrl,
    socketOptions,
    subscriber,
    defaultSchedule,
    context
  } = props;
  const novu = useNovu();
  const options = useMemo(() => {
    return {
      localization,
      appearance,
      tabs,
      preferencesFilter,
      preferenceGroups,
      preferencesSort,
      routerPush,
      options: {
        applicationIdentifier,
        subscriberHash,
        contextHash,
        backendUrl,
        socketUrl,
        socketOptions,
        subscriber: buildSubscriber({ subscriberId, subscriber }),
        defaultSchedule,
        context
      }
    };
  }, [
    localization,
    appearance,
    tabs,
    preferencesFilter,
    preferenceGroups,
    preferencesSort,
    applicationIdentifier,
    subscriberId,
    subscriberHash,
    contextHash,
    backendUrl,
    socketUrl,
    socketOptions,
    subscriber,
    context
  ]);
  if (isWithChildrenProps(props)) {
    return /* @__PURE__ */ jsx(NovuUI, { options, novu, children: props.children });
  }
  const {
    open,
    renderNotification,
    renderAvatar,
    renderSubject,
    renderBody,
    renderDefaultActions,
    renderCustomActions,
    renderBell,
    onNotificationClick,
    onPrimaryActionClick,
    onSecondaryActionClick,
    placementOffset,
    placement
  } = props;
  return /* @__PURE__ */ jsx(NovuUI, { options, novu, children: /* @__PURE__ */ jsx(
    DefaultInbox,
    {
      open,
      renderNotification,
      renderAvatar,
      renderSubject,
      renderBody,
      renderDefaultActions,
      renderCustomActions,
      renderBell,
      onNotificationClick,
      onPrimaryActionClick,
      onSecondaryActionClick,
      placement,
      placementOffset
    }
  ) });
});
InboxChild.displayName = "InboxChild";
function isWithChildrenProps(props) {
  return "children" in props;
}
export {
  Inbox
};
//# sourceMappingURL=Inbox.js.map